import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	AlertTriangle,
	EvComboboxOption,
	EvSection,
	EvSelectNew,
	EvSvgIcon,
	EvTitle
} from '@evinced-private/ui-common';

import { ICookieConsentConfiguration } from 'src/interfaces/CookieConsentConfiguration';
import { ILoginConfiguration } from 'src/interfaces/LoginConfiguration';
import { LOGIN_METHOD } from 'src/interfaces/LoginMethod';
import { useConfiguration } from 'src/providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from 'src/services/LocalTogglesService';
import { SETTINGS_SECTION_TYPES } from 'src/types/SettingsSectionTypes';

import { usePropertySettings } from '../context';
import { setLogin } from '../state';

import { CookiesLoginFields } from './CookiesLoginFields';
import { FormLoginFields } from './FormLoginFields';
import {
	LOGIN_METHOD_TO_TYPE,
	LOGIN_METHOD_TYPE,
	LOGIN_SETTINGS_DESCRIPTION_ID,
	LOGIN_SETTINGS_TITLE_ID,
	PASSWORD_MASK
} from './LoginSettingsV2.constants';
import { LOGIN_LABELS, LOGIN_MORE_INFO } from './LoginSettingsV2.labels';
import { FormLoginState, LoginMethodType } from './LoginSettingsV2.types';

import '../../../components/property-settings/sections/login-settings/LoginSettings.scss';

interface DispatchLoginParams {
	methodType: LoginMethodType;
	currentFormLogin: FormLoginState;
	currentCookies: string | null;
	currentCookiesConsentSelector: string;
}

export const LoginSettingsV2: FC = () => {
	const {
		state: { draft: property, original: originalProperty, errors },
		dispatch,
		registerErrorRef
	} = usePropertySettings();

	const propertyError = useMemo(
		() => errors.find((e) => e.sectionId === SETTINGS_SECTION_TYPES.LOGIN),
		[errors]
	);
	const { getToggle } = useConfiguration();
	const showCookiesConsentSelector = getToggle(
		SiteScannerUiToggles.ENABLE_COOKIES_CONSENT_SELECTOR
	);

	const scanLoginConfiguration = property.scanConfiguration?.loginConfiguration;
	const cookieConsentConfiguration =
		property.urlSetConfiguration?.discoveryConfiguration?.cookieConsentConfiguration ||
		property.scanConfiguration?.cookieConsentConfiguration;

	const initialCookies = scanLoginConfiguration?.cookies
		? JSON.stringify(scanLoginConfiguration.cookies)
		: null;

	const loginFormConfiguration = scanLoginConfiguration?.loginFormConfiguration;
	const originalScanConfigPassword =
		originalProperty?.scanConfiguration?.loginConfiguration?.loginFormConfiguration?.password;
	const originalUrlSetConfigPassword =
		originalProperty?.urlSetConfiguration?.discoveryConfiguration?.loginConfiguration
			?.loginFormConfiguration?.password;

	const [loginMethodType, setLoginMethodType] = useState<LoginMethodType>(() => {
		return (
			LOGIN_METHOD_TO_TYPE[scanLoginConfiguration?.loginMethod] ?? LOGIN_METHOD_TYPE.NOT_REQUIRED
		);
	});

	const [formLogin, setFormLogin] = useState<FormLoginState>(() => ({
		loginURL: loginFormConfiguration?.loginURL ?? '',
		userName: loginFormConfiguration?.userName ?? '',
		usernameSelector: loginFormConfiguration?.usernameSelector ?? '',
		password: loginFormConfiguration?.password ? PASSWORD_MASK : '',
		passwordSelector: loginFormConfiguration?.passwordSelector ?? '',
		loginButtonSelector: loginFormConfiguration?.loginButtonSelector ?? ''
	}));

	const [cookiesConsentSelector, setCookiesConsentSelector] = useState(
		cookieConsentConfiguration?.cookieButtonSelector ?? ''
	);
	const [cookies, setCookies] = useState(initialCookies);
	const [cookiesError, setCookieError] = useState<string | null>(null);

	const hasScanUserInteractionConfigurations = useMemo(
		() =>
			originalProperty?.scanConfiguration?.scraperConfiguration?.userInteractionConfiguration
				?.length > 0,
		[originalProperty?.scanConfiguration?.scraperConfiguration?.userInteractionConfiguration]
	);
	const hasUrlSetUserInteractionConfigurations = useMemo(
		() =>
			originalProperty?.urlSetConfiguration?.discoveryConfiguration?.userInteractionConfiguration
				?.length > 0,
		[originalProperty?.urlSetConfiguration?.discoveryConfiguration?.userInteractionConfiguration]
	);
	const hasLegacyConfig =
		hasScanUserInteractionConfigurations || hasUrlSetUserInteractionConfigurations;

	const buildAndDispatchLogin = useCallback(
		({
			methodType,
			currentFormLogin,
			currentCookies,
			currentCookiesConsentSelector
		}: DispatchLoginParams): void => {
			const cookieConsent: ICookieConsentConfiguration | null = showCookiesConsentSelector
				? { cookieButtonSelector: currentCookiesConsentSelector }
				: null;

			if (methodType === LOGIN_METHOD_TYPE.NOT_REQUIRED) {
				dispatch(setLogin(null, null, cookieConsent));
				return;
			}

			if (methodType === LOGIN_METHOD_TYPE.FORM_LOGIN) {
				const resolvePassword = (originalPassword: string): string =>
					currentFormLogin.password === PASSWORD_MASK
						? originalPassword
						: currentFormLogin.password;

				const createLoginConfig = (originalPassword: string): ILoginConfiguration => ({
					loginMethod: LOGIN_METHOD.LOGIN_FORM,
					loginFormConfiguration: {
						loginURL: currentFormLogin.loginURL,
						usernameSelector: currentFormLogin.usernameSelector,
						userName: currentFormLogin.userName,
						passwordSelector: currentFormLogin.passwordSelector,
						password: resolvePassword(originalPassword),
						loginButtonSelector: currentFormLogin.loginButtonSelector
					}
				});
				dispatch(
					setLogin(
						createLoginConfig(originalScanConfigPassword),
						createLoginConfig(originalUrlSetConfigPassword),
						cookieConsent
					)
				);
				return;
			}

			if (methodType === LOGIN_METHOD_TYPE.COOKIES) {
				let propertyCookies = null;
				if (currentCookies) {
					try {
						propertyCookies = JSON.parse(currentCookies);
						setCookieError(null);
					} catch {
						setCookieError(LOGIN_LABELS.INVALID_COOKIE_ERROR);
					}
				} else {
					setCookieError(LOGIN_LABELS.INVALID_COOKIE_ERROR);
				}
				const loginConfiguration: ILoginConfiguration = {
					loginMethod: LOGIN_METHOD.COOKIES,
					cookies: propertyCookies
				};
				dispatch(setLogin(loginConfiguration, loginConfiguration, cookieConsent));
			}
		},
		[dispatch, showCookiesConsentSelector, originalScanConfigPassword, originalUrlSetConfigPassword]
	);

	const handleLoginMethodChange = useCallback(
		(selectedItem: EvComboboxOption[]): void => {
			const newMethodType = selectedItem[0].value as LoginMethodType;
			setLoginMethodType(newMethodType);
			buildAndDispatchLogin({
				methodType: newMethodType,
				currentFormLogin: formLogin,
				currentCookies: cookies,
				currentCookiesConsentSelector: cookiesConsentSelector
			});
		},
		[formLogin, cookies, cookiesConsentSelector, buildAndDispatchLogin]
	);

	const handleFormFieldChange = useCallback(
		(field: keyof FormLoginState, value: string): void => {
			const updatedFormLogin = { ...formLogin, [field]: value };
			setFormLogin(updatedFormLogin);
			buildAndDispatchLogin({
				methodType: loginMethodType,
				currentFormLogin: updatedFormLogin,
				currentCookies: cookies,
				currentCookiesConsentSelector: cookiesConsentSelector
			});
		},
		[formLogin, loginMethodType, cookies, cookiesConsentSelector, buildAndDispatchLogin]
	);

	const handleCookiesChange = useCallback(
		(newCookies: string): void => {
			setCookies(newCookies);
			buildAndDispatchLogin({
				methodType: loginMethodType,
				currentFormLogin: formLogin,
				currentCookies: newCookies,
				currentCookiesConsentSelector: cookiesConsentSelector
			});
		},
		[loginMethodType, formLogin, cookiesConsentSelector, buildAndDispatchLogin]
	);

	const handleCookiesConsentChange = useCallback(
		(value: string): void => {
			setCookiesConsentSelector(value);
			buildAndDispatchLogin({
				methodType: loginMethodType,
				currentFormLogin: formLogin,
				currentCookies: cookies,
				currentCookiesConsentSelector: value
			});
		},
		[loginMethodType, formLogin, cookies, buildAndDispatchLogin]
	);

	const dropDownOptions = useMemo((): EvComboboxOption[] => {
		return [
			{ label: LOGIN_LABELS.NO_LOGIN_REQUIRED, value: LOGIN_METHOD_TYPE.NOT_REQUIRED },
			{
				label: LOGIN_LABELS.FORM_LOGIN,
				value: LOGIN_METHOD_TYPE.FORM_LOGIN,
				disabled: hasLegacyConfig
			},
			{
				label: LOGIN_LABELS.LOGIN_WITH_COOKIES,
				value: LOGIN_METHOD_TYPE.COOKIES,
				disabled: hasLegacyConfig
			}
		];
	}, [hasLegacyConfig]);

	const selectedDropDownValue = useMemo(
		() => [dropDownOptions.find((o) => o.value === loginMethodType)],
		[dropDownOptions, loginMethodType]
	);

	return (
		<EvSection
			className="login-settings-section"
			ariaLabelledby={LOGIN_SETTINGS_TITLE_ID}
			ariaDescribedby={LOGIN_SETTINGS_DESCRIPTION_ID}
		>
			<div className="login-settings-content">
				<div className="section-header">
					<div className="title">
						<EvTitle
							titleText={LOGIN_LABELS.SECTION_TITLE}
							MoreInfoContent={LOGIN_MORE_INFO}
							id={LOGIN_SETTINGS_TITLE_ID}
							descriptionId={LOGIN_SETTINGS_DESCRIPTION_ID}
						/>
					</div>
				</div>
				<div className="section-body">
					<div className="choose-login-method-dropdown">
						<EvSelectNew
							id="login-settings-select"
							options={dropDownOptions}
							selectedValue={selectedDropDownValue}
							onSelectChange={handleLoginMethodChange}
							placeholder={LOGIN_LABELS.SELECT_PLACEHOLDER}
							label={LOGIN_LABELS.SELECT_LABEL}
						/>
					</div>
					{hasLegacyConfig && (
						<div className="login-config-warning">
							<EvSvgIcon icon={AlertTriangle} isSmall />
							{LOGIN_LABELS.LEGACY_CONFIG_WARNING}
						</div>
					)}
					{!hasLegacyConfig && loginMethodType === LOGIN_METHOD_TYPE.FORM_LOGIN && (
						<FormLoginFields
							formLogin={formLogin}
							onFieldChange={handleFormFieldChange}
							showCookiesConsentSelector={showCookiesConsentSelector}
							cookiesConsentSelector={cookiesConsentSelector}
							onCookiesConsentChange={handleCookiesConsentChange}
						/>
					)}
					{!hasLegacyConfig && loginMethodType === LOGIN_METHOD_TYPE.COOKIES && (
						<CookiesLoginFields
							cookies={cookies}
							cookiesError={cookiesError}
							onCookiesChange={handleCookiesChange}
						/>
					)}
				</div>
				{propertyError && (
					<div
						ref={(el) => registerErrorRef(SETTINGS_SECTION_TYPES.LOGIN, el)}
						className="tab-error"
					>
						{propertyError.message}
					</div>
				)}
			</div>
		</EvSection>
	);
};
